import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend 
} from 'recharts';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/content';
import supabase from '../supabase/supabase';

const { 
  FiSave, FiPlusCircle, FiTrash2, FiActivity, FiCheckCircle, FiAlertCircle, FiLock, FiMail, FiLogIn, FiUserPlus, FiArrowRight, FiMove, FiTarget, FiScissors, FiTrendingDown, FiTrendingUp, FiZap
} = FiIcons;

const CustomAuthForm = ({ lang, onAuthSuccess }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      if (isLogin) {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      } else {
        const { error } = await supabase.auth.signUp({ email, password });
        if (error) throw error;
      }
      onAuthSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4 py-4 max-w-md mx-auto">
      <div className="flex p-1 bg-gray-100 rounded-lg mb-6">
        <button 
          onClick={() => setIsLogin(true)}
          className={`flex-1 py-2 text-sm font-bold rounded-md transition-all ${isLogin ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500'}`}
        >
          {lang === 'de' ? 'Anmelden' : 'Login'}
        </button>
        <button 
          onClick={() => setIsLogin(false)}
          className={`flex-1 py-2 text-sm font-bold rounded-md transition-all ${!isLogin ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500'}`}
        >
          {lang === 'de' ? 'Registrieren' : 'Sign Up'}
        </button>
      </div>

      {error && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="text-red-600 text-xs bg-red-50 p-3 rounded-lg border border-red-100 flex items-center space-x-2">
          <SafeIcon icon={FiAlertCircle} />
          <span>{error}</span>
        </motion.div>
      )}

      <div className="space-y-3">
        <div className="relative">
          <SafeIcon icon={FiMail} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input 
            type="email" 
            placeholder="Email" 
            value={email} 
            onChange={e => setEmail(e.target.value)} 
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none" 
          />
        </div>
        <div className="relative">
          <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input 
            type="password" 
            placeholder="Password" 
            value={password} 
            onChange={e => setPassword(e.target.value)} 
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none" 
          />
        </div>
      </div>

      <button 
        onClick={handleSubmit} 
        disabled={loading} 
        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-xl transition-all shadow-lg shadow-blue-200 disabled:opacity-50 flex items-center justify-center space-x-2"
      >
        {loading ? (
          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
        ) : (
          <>
            <SafeIcon icon={isLogin ? FiLogIn : FiUserPlus} />
            <span>{isLogin ? (lang === 'de' ? 'Jetzt Anmelden' : 'Login Now') : (lang === 'de' ? 'Konto erstellen' : 'Create Account')}</span>
          </>
        )}
      </button>
    </div>
  );
};

const Differentiation = () => {
  const { lang } = useLanguage();
  const t = translations[lang].differentiation;
  const dt = translations[lang].detail;
  const [session, setSession] = useState(null);
  const [useCase, setUseCase] = useState('');
  const [elements, setElements] = useState([
    { id: '1', name: lang === 'de' ? 'Preis' : 'Price', rating1: 50, rating2: 30, action: null },
    { id: '2', name: lang === 'de' ? 'Qualität' : 'Quality', rating1: 50, rating2: 40, action: null },
    { id: '3', name: lang === 'de' ? 'Geschwindigkeit' : 'Speed', rating1: 50, rating2: 60, action: null },
    { id: '4', name: lang === 'de' ? 'Design' : 'Design', rating1: 50, rating2: 20, action: null },
    { id: '5', name: lang === 'de' ? 'Service' : 'Service', rating1: 50, rating2: 70, action: null },
    { id: '6', name: lang === 'de' ? 'Exklusivität' : 'Exclusivity', rating1: 50, rating2: 10, action: null }
  ]);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [dragInfo, setDragInfo] = useState({ active: false, index: null, curve: 'curve1' });

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => setSession(session));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => setSession(session));
    return () => subscription?.unsubscribe();
  }, []);

  useEffect(() => {
    if (session) {
      const fetchData = async () => {
        const { data: settings } = await supabase
          .from('strategy_settings_1769582224082')
          .select('use_case')
          .eq('user_id', session.user.id)
          .single();
        if (settings) setUseCase(settings.use_case);

        const { data: elData } = await supabase
          .from('strategy_elements_1769582224082')
          .select('*')
          .eq('user_id', session.user.id)
          .order('sort_order', { ascending: true });

        if (elData && elData.length > 0) {
          setElements(elData.map(d => ({
            id: d.id,
            name: d.name,
            rating1: d.rating_offering,
            rating2: d.rating_competition,
            action: d.action || null
          })));
        }
      };
      fetchData();
      setShowAuthModal(false);
    }
  }, [session]);

  const handleElementChange = (id, field, value) => {
    setElements(prev => prev.map(el => el.id === id ? { ...el, [field]: value } : el));
  };

  const handleAddElement = () => {
    const newId = Date.now().toString();
    setElements(prev => [...prev, { id: newId, name: '', rating1: 50, rating2: 50, action: null }]);
  };

  const handleRemoveElement = (id) => {
    if (elements.length > 1) {
      setElements(prev => prev.filter(el => el.id !== id));
    }
  };

  const handleSave = async () => {
    if (!session) {
      setShowAuthModal(true);
      return;
    }
    setIsSaving(true);
    setMessage(null);
    try {
      await supabase.from('strategy_settings_1769582224082').upsert({ user_id: session.user.id, use_case: useCase });
      await supabase.from('strategy_elements_1769582224082').delete().eq('user_id', session.user.id);
      
      const elementsToInsert = elements.map((el, index) => ({
        user_id: session.user.id,
        name: el.name || (lang === 'de' ? 'Neues Element' : 'New Element'),
        rating_offering: el.rating1,
        rating_competition: el.rating2,
        action: el.action,
        sort_order: index
      }));
      await supabase.from('strategy_elements_1769582224082').insert(elementsToInsert);

      setMessage({ type: 'success', text: t.saved });
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setIsSaving(false);
    }
  };

  // Drag & Drop Logic
  const handleChartMouseDown = (e) => {
    if (e && e.activeTooltipIndex !== undefined) {
      const mouseY = e.chartY;
      // Chart area dimensions based on margin and height
      const areaHeight = 450 - 20 - 60; 
      const val1 = elements[e.activeTooltipIndex].rating1;
      const val2 = elements[e.activeTooltipIndex].rating2;
      const y1 = 20 + areaHeight * (1 - val1 / 100);
      const y2 = 20 + areaHeight * (1 - val2 / 100);
      
      const curve = Math.abs(mouseY - y1) < Math.abs(mouseY - y2) ? 'curve1' : 'curve2';
      setDragInfo({ active: true, index: e.activeTooltipIndex, curve });
    }
  };

  const handleChartMouseMove = (e) => {
    if (dragInfo.active && e && e.chartY) {
      const areaHeight = 450 - 20 - 60;
      const relativeY = e.chartY - 20;
      const newValue = Math.max(0, Math.min(100, Math.round(100 - (relativeY / areaHeight) * 100)));
      
      setElements(prev => prev.map((el, i) => 
        i === dragInfo.index ? { ...el, [dragInfo.curve === 'curve1' ? 'rating1' : 'rating2']: newValue } : el
      ));
    }
  };

  const handleChartMouseUp = () => {
    setDragInfo({ ...dragInfo, active: false });
  };

  const chartData = elements.map(el => ({
    name: el.name || '?',
    curve1: el.rating1,
    curve2: el.rating2
  }));

  const yAxisTickFormatter = (value) => {
    if (value === 20) return dt.step5Low;
    if (value === 50) return dt.step5Medium;
    if (value === 80) return dt.step5High;
    return '';
  };

  const processSteps = [
    { id: 1, title: t.process1, desc: t.process1Desc, icon: FiTarget },
    { id: 2, title: t.process2, desc: t.process2Desc, icon: FiActivity },
    { id: 3, title: t.process3, desc: t.process3Desc, icon: FiScissors },
    { id: 4, title: t.process4, desc: t.process4Desc, icon: FiZap }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-12 md:py-20">
      <div className="max-w-7xl mx-auto px-4">
        <div className="mb-16 text-center max-w-4xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center space-x-2 bg-blue-50 text-blue-600 px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest mb-6 border border-blue-100"
          >
            <SafeIcon icon={FiZap} />
            <span>Blue Ocean Strategy</span>
          </motion.div>
          <h1 className="text-4xl md:text-6xl font-black text-gray-900 tracking-tight leading-tight mb-6">{t.title}</h1>
          <p className="text-xl text-gray-600 leading-relaxed mb-8">{t.subtitle}</p>
          
          {/* Process Stepper Visualization with Animations */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-12 bg-white p-6 rounded-[2.5rem] shadow-sm border border-gray-100">
            {processSteps.map((step, i) => (
              <motion.div 
                key={step.id} 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15, duration: 0.5 }}
                className="relative flex flex-col items-center text-center p-4 group"
              >
                {/* Animated Icon Container */}
                <motion.div 
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 15 }}
                  className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center mb-4 group-hover:bg-blue-600 group-hover:text-white transition-all duration-300 shadow-sm group-hover:shadow-md"
                >
                  <SafeIcon icon={step.icon} className="text-xl" />
                </motion.div>

                {/* Content */}
                <h4 className="font-bold text-gray-900 text-sm mb-1 group-hover:text-blue-600 transition-colors">
                  {step.title}
                </h4>
                <p className="text-xs text-gray-400 leading-relaxed">{step.desc}</p>

                {/* Animated Arrow Between Steps */}
                {i < 3 && (
                  <motion.div 
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.15 + 0.3, duration: 0.5 }}
                    className="hidden md:block absolute -right-2 top-10 text-gray-300 group-hover:text-blue-500 transition-colors"
                  >
                    <motion.div
                      animate={{ x: [0, 5, 0] }}
                      transition={{ 
                        duration: 1.5, 
                        repeat: Infinity, 
                        ease: "easeInOut",
                        delay: i * 0.3
                      }}
                    >
                      <SafeIcon icon={FiArrowRight} className="text-lg" />
                    </motion.div>
                  </motion.div>
                )}

                {/* Step Number Badge */}
                <motion.div 
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.15 + 0.2, type: 'spring', stiffness: 200 }}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-[10px] font-black shadow-md"
                >
                  {step.id}
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          {/* Left: Element Editor */}
          <div className="bg-white rounded-[2.5rem] p-8 md:p-10 border shadow-sm space-y-8">
            <div>
              <label className="block text-sm font-black text-gray-900 uppercase tracking-widest mb-3">{dt.step5UseCase}</label>
              <textarea 
                value={useCase} 
                onChange={e => setUseCase(e.target.value)} 
                placeholder={dt.step5Placeholder}
                className="w-full p-5 bg-gray-50 border border-gray-100 rounded-2xl h-24 focus:ring-2 focus:ring-blue-500 outline-none transition-all resize-none italic font-medium"
              />
            </div>

            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-black text-gray-900 uppercase tracking-widest">{dt.step5Elements}</label>
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 rounded-full bg-violet-600 shadow-sm" />
                    <span className="text-[10px] uppercase font-black text-gray-400 tracking-tighter">{dt.step5Curve1}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 rounded-full bg-gray-400 shadow-sm" />
                    <span className="text-[10px] uppercase font-black text-gray-400 tracking-tighter">{dt.step5Curve2}</span>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2 custom-scrollbar">
                {elements.map((el) => (
                  <div key={el.id} className="p-6 bg-white rounded-3xl border border-gray-100 space-y-5 shadow-sm hover:shadow-md transition-all group relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-1 h-full bg-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="flex items-center justify-between gap-4">
                      <input 
                        type="text"
                        value={el.name}
                        onChange={(e) => handleElementChange(el.id, 'name', e.target.value)}
                        placeholder={lang === 'de' ? 'Wertfaktor Name' : 'Value Factor Name'}
                        className="flex-1 p-2 text-base font-bold bg-transparent border-b-2 border-transparent focus:border-blue-500 outline-none transition-colors"
                      />
                      {elements.length > 1 && (
                        <button 
                          onClick={() => handleRemoveElement(el.id)}
                          className="p-2 text-gray-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                        >
                          <SafeIcon icon={FiTrash2} />
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-black text-violet-600 uppercase tracking-widest">{dt.step5Curve1}</span>
                          <span className="text-[10px] font-mono font-black text-violet-600 bg-violet-50 px-2 py-0.5 rounded-full">{el.rating1}%</span>
                        </div>
                        <input 
                          type="range" min="0" max="100" value={el.rating1} 
                          onChange={(e) => handleElementChange(el.id, 'rating1', parseInt(e.target.value))}
                          className="w-full h-2 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-violet-600"
                        />
                      </div>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest">{dt.step5Curve2}</span>
                          <span className="text-[10px] font-mono font-black text-gray-500 bg-gray-50 px-2 py-0.5 rounded-full">{el.rating2}%</span>
                        </div>
                        <input 
                          type="range" min="0" max="100" value={el.rating2} 
                          onChange={(e) => handleElementChange(el.id, 'rating2', parseInt(e.target.value))}
                          className="w-full h-2 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-gray-400"
                        />
                      </div>
                    </div>
                    
                    {/* Action Selector (Eliminate/Reduce/Raise/Create) */}
                    <div className="flex flex-wrap gap-2 pt-2">
                       {['eliminate', 'reduce', 'raise', 'create'].map((action) => (
                         <button
                           key={action}
                           onClick={() => handleElementChange(el.id, 'action', el.action === action ? null : action)}
                           className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border transition-all ${
                             el.action === action 
                             ? (action === 'eliminate' || action === 'reduce' ? 'bg-red-600 border-red-600 text-white' : 'bg-green-600 border-green-600 text-white')
                             : 'bg-white border-gray-100 text-gray-400 hover:border-blue-200 hover:text-blue-600'
                           }`}
                         >
                           {t[action]}
                         </button>
                       ))}
                    </div>
                  </div>
                ))}
              </div>

              <button 
                onClick={handleAddElement}
                className="w-full py-4 border-2 border-dashed border-gray-100 rounded-3xl text-gray-400 hover:text-blue-600 hover:border-blue-200 hover:bg-blue-50/30 transition-all flex items-center justify-center space-x-2 font-black text-sm uppercase tracking-widest"
              >
                <SafeIcon icon={FiPlusCircle} />
                <span>{dt.step5AddElement}</span>
              </button>
            </div>

            <div className="pt-6 border-t border-gray-50 flex flex-col space-y-6">
              <div className="flex items-center justify-between">
                <button 
                  onClick={handleSave} 
                  disabled={isSaving} 
                  className="bg-blue-600 hover:bg-blue-700 text-white px-10 py-4 rounded-2xl font-black uppercase tracking-widest transition-all shadow-xl shadow-blue-200 flex items-center space-x-3 disabled:opacity-50"
                >
                  {isSaving ? <div className="w-5 h-5 border-3 border-white/30 border-t-white rounded-full animate-spin"></div> : <SafeIcon icon={FiSave} className="text-xl" />}
                  <span>{session ? dt.step5Save : (lang === 'de' ? 'Login zum Speichern' : 'Login to Save')}</span>
                </button>
                <AnimatePresence>
                  {message && (
                    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} className={`text-sm font-black uppercase tracking-widest ${message.type === 'success' ? 'text-green-600' : 'text-red-600'} flex items-center space-x-2`}>
                      <SafeIcon icon={message.type === 'success' ? FiCheckCircle : FiAlertCircle} />
                      <span>{message.text}</span>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {!session && showAuthModal && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="bg-blue-50/50 rounded-[2rem] p-8 border border-blue-100"
                >
                  <div className="text-center mb-6">
                    <p className="text-blue-800 font-bold mb-1 tracking-tight">{t.loginRequired}</p>
                    <p className="text-blue-600/60 text-xs italic">Save your strategy and access it anytime.</p>
                  </div>
                  <CustomAuthForm lang={lang} onAuthSuccess={() => setShowAuthModal(false)} />
                </motion.div>
              )}
            </div>
          </div>

          {/* Right: Strategy Chart */}
          <div className="bg-white rounded-[2.5rem] p-8 md:p-10 border shadow-sm flex flex-col sticky top-24 h-[600px]">
            <div className="flex items-center justify-between mb-10">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                  <SafeIcon icon={FiActivity} className="text-blue-600" />
                </div>
                <h3 className="font-black text-gray-900 uppercase tracking-widest">{dt.step5ChartTitle}</h3>
              </div>
              <div className="flex items-center space-x-2 bg-blue-50 px-4 py-1.5 rounded-full border border-blue-100">
                <SafeIcon icon={FiMove} className="text-blue-600 text-xs" />
                <span className="text-[10px] font-black text-blue-700 uppercase tracking-widest">
                  {lang === 'de' ? 'Klicken & Ziehen' : 'Click & Drag'}
                </span>
              </div>
            </div>
            
            <div className={`flex-1 select-none ${dragInfo.active ? 'cursor-ns-resize' : 'cursor-default'}`}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart 
                  data={chartData} 
                  margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                  onMouseDown={handleChartMouseDown}
                  onMouseMove={handleChartMouseMove}
                  onMouseUp={handleChartMouseUp}
                  onMouseLeave={handleChartMouseUp}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis 
                    dataKey="name" 
                    angle={-45} 
                    textAnchor="end" 
                    interval={0} 
                    height={80} 
                    tick={{ fontSize: 11, fontWeight: 900, fill: '#1f2937', textTransform: 'uppercase' }} 
                    axisLine={{ stroke: '#e2e8f0' }}
                  />
                  <YAxis 
                    domain={[0, 100]} 
                    ticks={[20, 50, 80]} 
                    tickFormatter={yAxisTickFormatter} 
                    tick={{ fontSize: 11, fontWeight: 900, fill: '#1f2937', textTransform: 'uppercase' }} 
                    axisLine={{ stroke: '#e2e8f0' }}
                  />
                  <Tooltip 
                    contentStyle={{ borderRadius: '20px', border: 'none', boxShadow: '0 25px 50px -12px rgb(0 0 0 / 0.1)', padding: '16px' }}
                    itemStyle={{ fontWeight: 800, fontSize: '12px', textTransform: 'uppercase' }}
                    trigger={dragInfo.active ? 'none' : 'hover'}
                  />
                  <Legend verticalAlign="top" height={50} wrapperStyle={{ fontWeight: 900, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b' }} />
                  <Line 
                    name={dt.step5Curve1} 
                    type="monotone" 
                    dataKey="curve1" 
                    stroke="#8b5cf6" 
                    strokeWidth={6} 
                    isAnimationActive={!dragInfo.active}
                    dot={{ r: 8, fill: '#8b5cf6', strokeWidth: 3, stroke: '#fff', cursor: 'grab' }} 
                    activeDot={{ r: 10, cursor: 'grabbing', strokeWidth: 0 }} 
                  />
                  <Line 
                    name={dt.step5Curve2} 
                    type="monotone" 
                    dataKey="curve2" 
                    stroke="#94a3b8" 
                    strokeWidth={3} 
                    strokeDasharray="8 8" 
                    isAnimationActive={!dragInfo.active}
                    dot={{ r: 6, fill: '#94a3b8', strokeWidth: 2, stroke: '#fff', cursor: 'grab' }} 
                    activeDot={{ r: 8, cursor: 'grabbing', strokeWidth: 0 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Four Actions Framework Visualization */}
        <section className="mt-24">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-4">{t.errcTitle}</h2>
            <p className="text-gray-500 max-w-2xl mx-auto">{t.errcSubtitle}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Eliminate & Reduce (Left Side - Red Tones) */}
            <div className="space-y-8">
              <div className="bg-red-50 p-8 rounded-[2.5rem] border border-red-100 relative overflow-hidden group">
                <div className="absolute top-0 right-0 p-6 opacity-10 group-hover:scale-110 transition-transform">
                  <SafeIcon icon={FiScissors} className="text-[120px] text-red-900" />
                </div>
                <h3 className="text-2xl font-black text-red-900 uppercase tracking-widest mb-4 flex items-center space-x-3">
                  <SafeIcon icon={FiScissors} />
                  <span>{t.eliminate}</span>
                </h3>
                <p className="text-red-800/70 font-medium mb-6 leading-relaxed italic">{t.eliminateDesc}</p>
                <div className="flex flex-wrap gap-2">
                  {elements.filter(el => el.action === 'eliminate').map(el => (
                    <span key={el.id} className="bg-white px-4 py-2 rounded-xl text-xs font-black text-red-600 border border-red-100 shadow-sm">{el.name}</span>
                  ))}
                  {elements.filter(el => el.action === 'eliminate').length === 0 && (
                    <span className="text-xs text-red-400 italic">No factors selected for elimination yet.</span>
                  )}
                </div>
              </div>

              <div className="bg-orange-50 p-8 rounded-[2.5rem] border border-orange-100 relative overflow-hidden group">
                <div className="absolute top-0 right-0 p-6 opacity-10 group-hover:scale-110 transition-transform">
                  <SafeIcon icon={FiTrendingDown} className="text-[120px] text-orange-900" />
                </div>
                <h3 className="text-2xl font-black text-orange-900 uppercase tracking-widest mb-4 flex items-center space-x-3">
                  <SafeIcon icon={FiTrendingDown} />
                  <span>{t.reduce}</span>
                </h3>
                <p className="text-orange-800/70 font-medium mb-6 leading-relaxed italic">{t.reduceDesc}</p>
                <div className="flex flex-wrap gap-2">
                  {elements.filter(el => el.action === 'reduce').map(el => (
                    <span key={el.id} className="bg-white px-4 py-2 rounded-xl text-xs font-black text-orange-600 border border-orange-100 shadow-sm">{el.name}</span>
                  ))}
                  {elements.filter(el => el.action === 'reduce').length === 0 && (
                    <span className="text-xs text-orange-400 italic">No factors selected for reduction yet.</span>
                  )}
                </div>
              </div>
            </div>

            {/* Raise & Create (Right Side - Green Tones) */}
            <div className="space-y-8">
              <div className="bg-blue-50 p-8 rounded-[2.5rem] border border-blue-100 relative overflow-hidden group">
                <div className="absolute top-0 right-0 p-6 opacity-10 group-hover:scale-110 transition-transform">
                  <SafeIcon icon={FiTrendingUp} className="text-[120px] text-blue-900" />
                </div>
                <h3 className="text-2xl font-black text-blue-900 uppercase tracking-widest mb-4 flex items-center space-x-3">
                  <SafeIcon icon={FiTrendingUp} />
                  <span>{t.raise}</span>
                </h3>
                <p className="text-blue-800/70 font-medium mb-6 leading-relaxed italic">{t.raiseDesc}</p>
                <div className="flex flex-wrap gap-2">
                  {elements.filter(el => el.action === 'raise').map(el => (
                    <span key={el.id} className="bg-white px-4 py-2 rounded-xl text-xs font-black text-blue-600 border border-blue-100 shadow-sm">{el.name}</span>
                  ))}
                  {elements.filter(el => el.action === 'raise').length === 0 && (
                    <span className="text-xs text-blue-400 italic">No factors selected for raising yet.</span>
                  )}
                </div>
              </div>

              <div className="bg-green-50 p-8 rounded-[2.5rem] border border-green-100 relative overflow-hidden group">
                <div className="absolute top-0 right-0 p-6 opacity-10 group-hover:scale-110 transition-transform">
                  <SafeIcon icon={FiPlusCircle} className="text-[120px] text-green-900" />
                </div>
                <h3 className="text-2xl font-black text-green-900 uppercase tracking-widest mb-4 flex items-center space-x-3">
                  <SafeIcon icon={FiPlusCircle} />
                  <span>{t.create}</span>
                </h3>
                <p className="text-green-800/70 font-medium mb-6 leading-relaxed italic">{t.createDesc}</p>
                <div className="flex flex-wrap gap-2">
                  {elements.filter(el => el.action === 'create').map(el => (
                    <span key={el.id} className="bg-white px-4 py-2 rounded-xl text-xs font-black text-green-600 border border-green-100 shadow-sm">{el.name}</span>
                  ))}
                  {elements.filter(el => el.action === 'create').length === 0 && (
                    <span className="text-xs text-green-400 italic">No factors created yet.</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Differentiation;